import { toPng } from 'html-to-image'

export interface AvatarConfig {
  accessory: 'none' | 'roundGlasses' | 'tinyGlasses'
  body: 'chest' | 'breasts'
  circleColor: 'blue'
  clothing: 'shirt' | 'vneck' | 'tankTop' | 'dress'
  clothingColor: 'white' | 'blue' | 'black'
  eyebrows: 'raised' | 'leftLowered' | 'serious'
  eyes: 'normal' | 'leftTwitch' | 'happy' | 'wink' | 'heart'
  facialHair: 'none' | 'stubble' | 'mediumBeard'
  graphic: 'none' | 'redwood' | 'gatsby' | 'vue' | 'react' | 'graphQL'
  hair: 'long' | 'bun' | 'short' 
  hairColor: 'blonde' | 'orange' | 'black' | 'white' | 'brown' | 'blue' | 'pink'
  hat: 'none' | 'beanie'
  hatColor: 'white' | 'blue' | 'black' | 'green' | 'red'
  lashes: boolean
  lipColor: 'red' | 'purple' | 'pink'
  mask: boolean
  faceMask: boolean
  mouth: 'grin' | 'sad' | 'openSmile' 
  skinTone: 'light' | 'yellow' | 'brown' | 'dark'
}

export const AVATAR_OPTIONS = {
  accessory: [
    { value: 'none' as const, label: 'None' },
    { value: 'roundGlasses' as const, label: 'Round Glasses' },
    { value: 'tinyGlasses' as const, label: 'Tiny Glasses' },
  ],
  body: [
    { value: 'chest' as const, label: 'Chest' },
    { value: 'breasts' as const, label: 'Breasts' },
  ],
  circleColor: [
    { value: 'blue' as const, label: 'Blue' },
  ],
  clothing: [
    { value: 'shirt' as const, label: 'Shirt' },
    { value: 'vneck' as const, label: 'V-Neck' },
    { value: 'tankTop' as const, label: 'Tank Top' },
    { value: 'dress' as const, label: 'Dress' },
  ],
  clothingColor: [
    { value: 'white' as const, label: 'White' },
    { value: 'blue' as const, label: 'Blue' },
    { value: 'black' as const, label: 'Black' },
  ],
  eyebrows: [
    { value: 'raised' as const, label: 'Raised' },
    { value: 'leftLowered' as const, label: 'Left Lowered' },
    { value: 'serious' as const, label: 'Serious' },
  ],
  eyes: [
    { value: 'normal' as const, label: 'Normal' },
    { value: 'leftTwitch' as const, label: 'Left Twitch' },
    { value: 'happy' as const, label: 'Happy' },
    { value: 'wink' as const, label: 'Wink' },
    { value: 'heart' as const, label: 'Heart' },
  ],
  facialHair: [
    { value: 'none' as const, label: 'None' },
    { value: 'stubble' as const, label: 'Stubble' },
    { value: 'mediumBeard' as const, label: 'Medium Beard' },
  ],
  graphic: [
    { value: 'none' as const, label: 'None' },
    { value: 'redwood' as const, label: 'Redwood' },
    { value: 'gatsby' as const, label: 'Gatsby' },
    { value: 'vue' as const, label: 'Vue' },
    { value: 'react' as const, label: 'React' },
    { value: 'graphQL' as const, label: 'GraphQL' },
  ],
  hair: [
    { value: 'long' as const, label: 'Long' },
    { value: 'bun' as const, label: 'Bun' },
    { value: 'short' as const, label: 'Short' },
  ],
  hairColor: [
    { value: 'blonde' as const, label: 'Blonde' },
    { value: 'orange' as const, label: 'Orange' },
    { value: 'black' as const, label: 'Black' },
    { value: 'white' as const, label: 'White' },
    { value: 'brown' as const, label: 'Brown' },
    { value: 'blue' as const, label: 'Blue' },
    { value: 'pink' as const, label: 'Pink' },
  ],
  hat: [
    { value: 'none' as const, label: 'None' },
    { value: 'beanie' as const, label: 'Beanie' },
  ],
  hatColor: [
    { value: 'white' as const, label: 'White' },
    { value: 'blue' as const, label: 'Blue' },
    { value: 'black' as const, label: 'Black' },
    { value: 'green' as const, label: 'Green' },
    { value: 'red' as const, label: 'Red' },
  ],
  lipColor: [
    { value: 'red' as const, label: 'Red' },
    { value: 'purple' as const, label: 'Purple' },
    { value: 'pink' as const, label: 'Pink' },
  ],
  mouth: [
    { value: 'grin' as const, label: 'Grin' },
    { value: 'sad' as const, label: 'Sad' },
    { value: 'openSmile' as const, label: 'Open Smile' },
  ],
  skinTone: [
    { value: 'light' as const, label: 'Light' },
    { value: 'yellow' as const, label: 'Yellow' },
    { value: 'brown' as const, label: 'Brown' },
    { value: 'dark' as const, label: 'Dark' },
  ],
}

export const DEFAULT_AVATAR_CONFIG: AvatarConfig = {
  accessory: 'none',
  body: 'chest',
  circleColor: 'blue',
  clothing: 'shirt',
  clothingColor: 'blue',
  eyebrows: 'raised',
  eyes: 'normal',
  facialHair: 'none',
  graphic: 'none',
  hair: 'short',
  hairColor: 'brown',
  hat: 'none',
  hatColor: 'blue',
  lashes: false,
  lipColor: 'red',
  mask: false,
  faceMask: false,
  mouth: 'grin',
  skinTone: 'light',
}

export const randomizeAvatarConfig = (): AvatarConfig => {
  const getRandomOption = <T extends string>(options: Array<{ value: T; label: string }>) => {
    return options[Math.floor(Math.random() * options.length)].value
  }

  return {
    accessory: 'none', // Hidden accessory tab - always use none
    body: getRandomOption(AVATAR_OPTIONS.body),
    circleColor: getRandomOption(AVATAR_OPTIONS.circleColor),
    clothing: getRandomOption(AVATAR_OPTIONS.clothing),
    clothingColor: getRandomOption(AVATAR_OPTIONS.clothingColor),
    eyebrows: getRandomOption(AVATAR_OPTIONS.eyebrows),
    eyes: getRandomOption(AVATAR_OPTIONS.eyes),
    facialHair: getRandomOption(AVATAR_OPTIONS.facialHair),
    graphic: 'none', // Hidden accessory tab - always use none
    hair: getRandomOption(AVATAR_OPTIONS.hair),
    hairColor: getRandomOption(AVATAR_OPTIONS.hairColor),
    hat: 'none', // Hidden accessory tab - always use none
    hatColor: getRandomOption(AVATAR_OPTIONS.hatColor),
    lashes: Math.random() > 0.5,
    lipColor: getRandomOption(AVATAR_OPTIONS.lipColor),
    mask: false, // Hidden accessory tab - always false
    faceMask: false, // Hidden accessory tab - always false
    mouth: getRandomOption(AVATAR_OPTIONS.mouth),
    skinTone: getRandomOption(AVATAR_OPTIONS.skinTone),
  }
}

export const convertSVGToBlob = async (element: HTMLElement): Promise<Blob> => {
  const dataUrl = await toPng(element, {
    quality: 1,
    pixelRatio: 2,
    backgroundColor: 'transparent',
    skipFonts: true, // Skip external fonts to avoid CORS issues
    style: {
      // Use system fonts instead of external fonts
      fontFamily: 'system-ui, -apple-system, sans-serif',
    },
  })
  
  const response = await fetch(dataUrl)
  return response.blob()
}

